import { Check, ChevronLeft, ChevronRight, ImageIcon, Upload, X } from 'lucide-react';
import { useState } from 'react';

import { MediaRefImage } from '@/components/media-ref-image';
import { MediaThumbnail } from '@/components/media-thumbnail';
import { MediaUploadDialog } from '@/components/media-upload-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useMedia } from '@/lib/api-hooks';
import {
  type MediaItem,
  type MediaRef,
  type MediaType,
  mediaSizeLabel,
  mediaTypes,
} from '@/lib/media';
import { cn } from '@/lib/utils';

type BaseProps = {
  /** Restrict the picker to these media types (defaults to all). */
  types?: MediaType[];
};

type SingleProps = BaseProps & {
  multiple?: false;
  value?: MediaRef | null;
  onChange: (media: MediaRef | null) => void;
};

type MultipleProps = BaseProps & {
  multiple: true;
  values?: MediaRef[];
  onChange: (media: MediaRef[]) => void;
};

type LibraryPickerProps = SingleProps | MultipleProps;

const PICKER_PAGE_SIZE = 12;
const GRID_CLASS = 'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4';
const SKELETON_KEYS = Array.from({ length: PICKER_PAGE_SIZE }, (_, index) => `skeleton-${index}`);

/** A media-library picker: pick one or many assets, or upload new ones. */
export function LibraryPicker(props: LibraryPickerProps) {
  const multiple = props.multiple === true;
  const allowedTypes = props.types ?? mediaTypes;

  const [open, setOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<'all' | MediaType>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<MediaRef[]>([]);

  // With a single allowed type there's nothing to filter, so query it directly
  // and skip the filter UI entirely.
  const singleType = allowedTypes.length === 1 ? allowedTypes[0] : undefined;
  const effectiveType: 'all' | MediaType = singleType ?? type;

  const { data, isLoading } = useMedia({
    search,
    type: effectiveType,
    order: 'newest',
    page,
    pageSize: PICKER_PAGE_SIZE,
  });
  const items = (data?.items ?? []).filter((item) => allowedTypes.includes(item.type));
  const currentPage = data?.page ?? page;
  const totalPages = data?.totalPages ?? 1;

  const typeFilters: Array<'all' | MediaType> = ['all', ...allowedTypes];

  function openPicker() {
    if (props.multiple === true) setSelected(props.values ?? []);
    setPage(1);
    setOpen(true);
  }

  function choose(item: MediaRef) {
    if (props.multiple === true) {
      setSelected((prev) =>
        prev.some((entry) => entry.id === item.id)
          ? prev.filter((entry) => entry.id !== item.id)
          : [...prev, item],
      );
    } else {
      props.onChange(item);
      setOpen(false);
    }
  }

  function handleUploaded(uploaded: MediaItem[]) {
    if (props.multiple === true) {
      setSelected((prev) => [...prev, ...uploaded]);
    } else if (uploaded[0]) {
      props.onChange(uploaded[0]);
      setOpen(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      {props.multiple === true ? (
        <SelectedGallery values={props.values ?? []} onChange={props.onChange} />
      ) : (
        <SelectedPreview item={props.value ?? null} />
      )}

      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={openPicker}>
          {props.multiple === true
            ? (props.values ?? []).length > 0
              ? 'Edit gallery'
              : 'Choose images'
            : props.value
              ? 'Change'
              : 'Choose from library'}
        </Button>
        {props.multiple !== true && props.value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => props.onChange(null)}>
            <X />
            Remove
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader className="flex-row items-center justify-between gap-2 sm:flex-row">
            <div className="grid gap-1.5">
              <DialogTitle>Media library</DialogTitle>
              <DialogDescription>
                {props.multiple === true ? 'Select assets.' : 'Pick an asset to use.'}
              </DialogDescription>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setUploadOpen(true)}
              className="shrink-0"
            >
              <Upload />
              Upload
            </Button>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search media"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="sm:max-w-xs"
            />
            {allowedTypes.length > 1 && (
              <div className="flex flex-wrap gap-1">
                {typeFilters.map((filter) => (
                  <Button
                    key={filter}
                    type="button"
                    size="sm"
                    variant={type === filter ? 'default' : 'ghost'}
                    onClick={() => {
                      setType(filter);
                      setPage(1);
                    }}
                    className="h-8 capitalize"
                  >
                    {filter}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="h-[50vh] overflow-y-auto pr-1">
            {isLoading ? (
              <div className={GRID_CLASS}>
                {SKELETON_KEYS.map((id) => (
                  <SkeletonCard key={id} />
                ))}
              </div>
            ) : items.length === 0 ? (
              <p className="py-10 text-center text-muted-foreground text-sm">No media found.</p>
            ) : (
              <div className={GRID_CLASS}>
                {items.map((item) => {
                  const isSelected =
                    props.multiple === true
                      ? selected.some((entry) => entry.id === item.id)
                      : props.value?.id === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => choose(item)}
                      className={cn(
                        'relative overflow-hidden rounded-md border bg-card text-left transition-colors hover:border-ring',
                        isSelected && 'border-primary ring-1 ring-primary',
                      )}
                    >
                      {isSelected && (
                        <span className="absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="size-3" />
                        </span>
                      )}
                      <MediaThumbnail item={item} className="aspect-video w-full" />
                      <div className="space-y-0.5 p-2">
                        <p className="truncate font-medium text-xs">{item.filename}</p>
                        <p className="text-muted-foreground text-[10px]">{mediaSizeLabel(item)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-xs">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={currentPage <= 1 || isLoading}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                >
                  <ChevronLeft />
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  disabled={currentPage >= totalPages || isLoading}
                  onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                >
                  Next
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}

          {props.multiple === true && (
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  props.onChange(selected);
                  setOpen(false);
                }}
              >
                {selected.length > 0 ? `Add ${selected.length}` : 'Add'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <MediaUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={handleUploaded}
      />
    </div>
  );
}

/** Placeholder that mirrors an asset card, so the grid keeps its height. */
function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="aspect-video w-full animate-pulse bg-muted" />
      <div className="space-y-1.5 p-2">
        <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-2.5 w-1/3 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

/** Multi-select display: the chosen images, each removable inline. */
function SelectedGallery({
  values,
  onChange,
}: {
  values: MediaRef[];
  onChange: (values: MediaRef[]) => void;
}) {
  if (values.length === 0) {
    return <span className="text-muted-foreground text-sm">No images selected</span>;
  }

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= values.length) return;
    const next = [...values];
    const [entry] = next.splice(index, 1);
    if (entry) next.splice(target, 0, entry);
    onChange(next);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {values.map((media, index) => (
        <div key={media.id} className="group relative size-12 overflow-hidden rounded-md border">
          <MediaRefImage media={media} className="h-full w-full" />
          <button
            type="button"
            onClick={() => onChange(values.filter((entry) => entry.id !== media.id))}
            aria-label="Remove image"
            className="absolute top-0.5 right-0.5 flex size-4 items-center justify-center rounded-full bg-background/90 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
          >
            <X className="size-3" />
          </button>
          <div className="absolute inset-x-0 bottom-0 flex justify-between opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              onClick={() => move(index, -1)}
              disabled={index === 0}
              aria-label="Move left"
              className="flex size-4 items-center justify-center bg-background/90 text-muted-foreground hover:text-foreground disabled:opacity-30"
            >
              <ChevronLeft className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => move(index, 1)}
              disabled={index === values.length - 1}
              aria-label="Move right"
              className="flex size-4 items-center justify-center bg-background/90 text-muted-foreground hover:text-foreground disabled:opacity-30"
            >
              <ChevronRight className="size-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SelectedPreview({ item }: { item: MediaRef | null }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
      {item?.url && !failed ? (
        <img
          src={item.url}
          alt="Selected"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <ImageIcon className="size-5 text-muted-foreground" />
      )}
    </div>
  );
}
